/*** IMPORTS FROM imports-loader ***/
import hydrator from "@gjensidige/views-api/src/runtime/hydrator";
import Component from ".";

/* global hydrator, Component */

// create a map of what we have hydrated, share it on window
window.__HYDRATED__ = window.__HYDRATED__ || {};

// get the current script executing
const partSrc = window.document?.currentScript?.getAttribute('src');

// filter the ones that need to be hydrated (data-nodeid contains the id for the props -> only hydration on node id's)
const parts = Array.from(
  document.querySelectorAll('script[src="' + partSrc + '"]')
).filter((part) => part.getAttribute('data-nodeid'));

// if this selection of parts contains elements that needs to be hydrated, add an eventListener
if (parts.length && window.addEventListener) {
  window.addEventListener('load', function () {
    parts.forEach((part) => {
      // make sure we only hydrate our components once
      const NODE_ID = part.getAttribute('data-nodeid');
      if (!window.__HYDRATED__[NODE_ID]) {
        window.__HYDRATED__[NODE_ID] = true;
        hydrator(Component, NODE_ID);
      }
    });
  });
}


import React, { useContext, useEffect, useRef, useState } from 'react';
import { SearchResultItem, SearchForm as Props } from '@internal/schema-client';
import { canUseDOM } from '@internal/utils/can-use-dom';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types/prop-types';
import {
  Link,
  Flex,
  Input,
  Label,
  Text,
  Title
} from '@gjensidige/builders-components';
import { trackEvent } from '@gjensidige/core-analytics/lib/track-event';
import { TrackingContext } from '@gjensidige/core-analytics/lib/tracking-context';
import { TrackEventConfig } from '@gjensidige/core-analytics/lib/types';

const DEFAULT_TRACKING: TrackEventConfig = {
  eventCategory: 'input',
  eventLabel: 'site-search',
  eventKey: 'input'
};

import styles from '../less/search.module.less';

export const SearchPage = ({
  inputLabel,
  messages,
  searchAPIUrl,
  query
}: Props) => {
  const { options: trackingOptions } = useContext(TrackingContext);

  const searchInput = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState(query ?? '');
  const [items, setItems] = useState<SearchResultItem[]>([]);
  const [noResultsCheck, setNoResultsCheck] = useState(false);
  const [loading, setLoading] = useState(false);
  const controllerRef = useRef<AbortController | null>();
  const [searchFinished, setSearchFinished] = useState(false);

  const debouncedSearch = useRef(
    debounce(async (query) => {
      if (!searchAPIUrl) return;
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      const controller = new AbortController();
      controllerRef.current = controller;
      const res = await fetch(searchAPIUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query }),
        signal: controllerRef.current?.signal
      });
      const data = await res.json();
      setLoading(false);
      setItems(data);
      data.length === 0 && setNoResultsCheck(true);
      setSearchFinished(true);
      controllerRef.current = null;
    }, 500)
  ).current;

  useEffect(() => {
    searchInput?.current?.focus();
  }, []);

  useEffect(() => {
    setSearchFinished(false);
    if (inputValue.length >= 3 && canUseDOM()) {
      setItems([]);
      setNoResultsCheck(false);
      setLoading(true);
      debouncedSearch(inputValue);
    } else {
      setItems([]);
      setLoading(false);
      setNoResultsCheck(false);
      setSearchFinished(true);
    }
  }, [debouncedSearch, inputValue]);

  useEffect(() => {
    if (searchFinished) {
      if (items?.length > 0) {
        trackEvent(
          {
            ...DEFAULT_TRACKING,
            eventLabel: inputValue,
            eventCategory: 'site-search',
            eventAction: 'search',
            eventStatus: 'success'
          },
          trackingOptions
        );
      } else {
        if (inputValue.length >= 3 && canUseDOM()) {
          trackEvent(
            {
              eventLabel: inputValue,
              eventCategory: 'site-search',
              eventAction: 'search',
              eventStatus: 'error'
            },
            trackingOptions
          );
        }
      }
    }
  }, [searchFinished]);

  const nrOfHits =
    items?.length == 1
      ? messages.result.hit
      : messages.result?.hits
      ? messages.result.hits.replace('!!', items?.length.toString())
      : 'hits';

  return (
    <Flex
      center
      className={classNames(styles.searchContainer, styles.marginYParts)}
      layout={{ xs: 1, lg: 2 }}
      gap="sm xl"
    >
      <div>
        <Title as="h1" size="3">
          {messages.information.title}
        </Title>
        <Text size="lead" className={styles.infoText}>
          {messages.information.description}
        </Text>
      </div>
      <div>
        <Label className={styles.searchBar}>
          {inputLabel}
          <Input
            id="search"
            name="q"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setInputValue(event.currentTarget.value)
            }
            value={inputValue}
            tracking={false}
            type="search"
            ref={searchInput}
          />
        </Label>

        {loading && (
          <div>
            <div className={styles.loadingTitle} />
            <div className={styles.loadingText} />
          </div>
        )}

        {items?.length > 0 && (
          <>
            <Text aria-live="polite" size="small" className={styles.resultText}>
              {messages.result.title} <span>{nrOfHits && nrOfHits}</span>
            </Text>
            <Flex
              className={styles.products}
              data-title={messages.result?.products || undefined}
              gap={{ xs: 'sm xs', md: 'sm' }}
            >
              {items.map(
                ({ product }) =>
                  product && <Link href={product.url}>{product.text}</Link>
              )}
            </Flex>
            {items.map((searchItem: SearchResultItem, index) => (
              <SearchItem key={`${searchItem._id}_${index}`} {...searchItem} />
            ))}
          </>
        )}

        {noResultsCheck && (
          <div>
            <Title size="5">{messages.noResult?.title}</Title>
            <Text size="small">{messages.noResult?.description}</Text>
          </div>
        )}
      </div>
    </Flex>
  );
};

SearchPage.propTypes = {
  action: PropTypes.any,
  inputLabel: PropTypes.string,
  messages: PropTypes.any,
  searchAPIUrl: PropTypes.string,
  query: PropTypes.string
};

SearchPage.CSR = true;

export default SearchPage;

const SearchItem = ({ title, preface, link }: SearchResultItem) => (
  <a href={link.url} className={styles.searchItem}>
    <div className={styles.searchItemResult}>
      <Title size="5" className={styles.searchItemTitle} text={title} />
      {preface && (
        <Text size="small" className={styles.searchItemText}>
          {preface}
        </Text>
      )}
      <Text size="small" className={styles.searchItemBreadcrumb}>
        {breadCrumbManipulator(link.url)}
      </Text>
    </div>
  </a>
);

const breadCrumbManipulator = (target: string) => {
  const hyphen = /-/gi;
  const slash = /\//gi;
  let temp = target.slice(1);
  temp = temp.replace(hyphen, ' ');
  temp = temp.replace(slash, ' → ');
  temp = temp.replace(
    /(^\w{1})|(\s+\w{1})/g,
    (letter) => letter?.toUpperCase()
  );
  return temp;
};
